import React, { useState } from "react";
import { useFormik } from "formik";
import { Modal } from "reactstrap";
import { toast } from "react-toastify";

import { useWorkplacesClient } from "../../../api/grpc/workplaces/useWorkplacesClient";
import { DeviceTreeDevice } from "./lib/deviceTree";
import { DateTimeSchema, toDeviceLocale } from "../lib/deviceDateTime";

import { Button } from "../../shared/Button/Button";
import { CalendarsWizardHeaderItem } from "../../Calendars/CalendarsWizardForm/CalendarsWizardHeader/CalendarsWizardHeaderItem/CalendarsWizardHeaderItem";
import { StepProps } from "../../Calendars/CalendarsWizardForm/CalendarsWizardForm";
import {
  DateTimeFormFields,
  DateTimeSection,
} from "../../shared/Forms/DeviceForm/sections/DateTimeSection";
import { DeviceTimePicker } from "./DeviceTimePicker";
import { SelectedDevices } from "./SelectedDevices";

import "../../Calendars/CalendarsWizardForm/CalendarsWizardHeader/CalendarsWizardHeader.scss";
import "./DeviceTimeWizard.scss";

enum StepsEnum {
  SELECT_DEVICES = 1,
  CONFIGURE = 2,
}

const STEPS: StepProps[] = [
  { id: "device-time-wizard-1", title: "Select target devices" },
  { id: "device-time-wizard-2", title: "Configure date & time" },
];

// The step headers only show progress; the footer buttons move between steps
const noop = () => {};

// The Device Modal's defaults
const INITIAL_VALUES: DateTimeFormFields = {
  timeZone: "",
  locale: "",
  autoTime: false,
  use24HourTime: true,
};

// The error toast names this many failed devices and counts the rest
const MAX_NAMED_DEVICES = 3;

const listDeviceNames = (devices: DeviceTreeDevice[]) => {
  const names = devices
    .slice(0, MAX_NAMED_DEVICES)
    .map(({ name }) => name)
    .join(", ");
  const rest = devices.length - MAX_NAMED_DEVICES;

  return rest > 0 ? `${names} and ${rest} more` : names;
};

interface Props {
  // The account's devices that support Date & time settings
  devices: DeviceTreeDevice[];
  onClose: () => void;
  // Reloads the device list after a save
  refetch?: () => void;
}

// Mounted only while open, so closing it resets the steps and the selection
export const DeviceTimeWizard = ({ devices, onClose, refetch }: Props) => {
  const [activeStep, setActiveStep] = useState<StepsEnum>(
    StepsEnum.SELECT_DEVICES
  );
  const [selectedDevices, setSelectedDevices] = useState<DeviceTreeDevice[]>(
    []
  );
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const { updateRDXDevicesDateTimeSettings } = useWorkplacesClient();

  const saveSettings = async ({
    timeZone,
    locale,
    autoTime,
    use24HourTime,
  }: DateTimeFormFields) => {
    // The tree's devices come from the workplaces query, which has no deviceId
    // or generation
    const devicesById = new Map(devices.map((device) => [device.id, device]));
    const toDeviceId = (id: string) => devicesById.get(id)?.deviceId || "";

    // Gen 1 and Gen 2 can take different tags for the same locale, so each
    // tag goes in its own request
    const deviceIdsByLocale = new Map<string, string[]>();

    selectedDevices.forEach(({ id }) => {
      const deviceLocale = toDeviceLocale(
        locale ?? "",
        devicesById.get(id)?.softwareInfo?.rdxGenVersion
      );

      deviceIdsByLocale.set(deviceLocale, [
        ...(deviceIdsByLocale.get(deviceLocale) || []),
        toDeviceId(id),
      ]);
    });

    const requests = Array.from(deviceIdsByLocale);

    try {
      const results = await Promise.allSettled(
        requests.map(([deviceLocale, deviceIds]) =>
          updateRDXDevicesDateTimeSettings({
            deviceIds,
            timezone: timeZone ?? "",
            locale: deviceLocale,
            autoTimeEnabled: !!autoTime,
            format24HEnabled: !!use24HourTime,
          })
        )
      );
      const rejected = results.filter(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );

      // Nothing was saved
      if (rejected.length === results.length) {
        throw rejected[0].reason;
      }

      rejected.forEach(({ reason }) => console.error(reason));

      // A rejected request's devices count as failed
      const failedDeviceIds = results.flatMap((result, index) =>
        result.status === "fulfilled"
          ? result.value.response.failedDeviceIds
          : requests[index][1]
      );

      // Some devices may have been saved even when others failed
      refetch?.();

      if (!failedDeviceIds.length) {
        toast.success("Date and time settings saved!");
        onClose();
        return;
      }

      // Only the failed devices stay selected, so Save retries them
      const failedIds = new Set(failedDeviceIds);
      const failedDevices = selectedDevices.filter(({ id }) =>
        failedIds.has(toDeviceId(id))
      );

      setSelectedDevices(failedDevices);
      toast.error(
        `Date and time settings couldn't be saved on ${listDeviceNames(
          failedDevices
        )}, please try again!`
      );
    } catch (error: any) {
      console.error(error);
      toast.error(
        error?.message ||
          "Date and time settings couldn't be saved, please try again!"
      );
    }
  };

  const formik = useFormik<DateTimeFormFields>({
    initialValues: INITIAL_VALUES,
    validationSchema: DateTimeSchema,
    onSubmit: saveSettings,
  });

  const closePicker = () => setIsPickerOpen(false);

  const removeDevice = (device: DeviceTreeDevice) =>
    setSelectedDevices((prev) => prev.filter(({ id }) => id !== device.id));

  return (
    <Modal
      isOpen
      centered
      size="lg"
      backdrop="static"
      // Closing mid-save would let the late onClose reopen it (it toggles)
      keyboard={!formik.isSubmitting}
      // Esc closes the picker first, then the wizard
      toggle={isPickerOpen ? closePicker : onClose}
      className="DeviceTimeWizard"
    >
      <div className="DeviceTimeWizard__body">
        <div className="CalendarsWizardHeader">
          {STEPS.map((item, index) => {
            const number = index + 1;

            return (
              <CalendarsWizardHeaderItem
                key={item.id}
                item={item}
                number={number}
                isActive={activeStep === number}
                isCompleted={number < activeStep}
                onClick={noop}
              />
            );
          })}
        </div>

        <div className="DeviceTimeWizard__content">
          {activeStep === StepsEnum.SELECT_DEVICES ? (
            <>
              <Button
                size="small"
                color="outline-primary"
                title="Select devices"
                onClick={() => setIsPickerOpen(true)}
              />
              <p className="DeviceTimeWizard__hint mt-3 mb-1">
                Pick the devices whose date and time settings you want to
                change.
              </p>
              <p className="DeviceTimeWizard__hint text-danger mb-0">
                Only supported devices are available for selection.
              </p>

              <SelectedDevices
                devices={selectedDevices}
                onRemove={removeDevice}
              />
            </>
          ) : (
            <DateTimeSection formik={formik} />
          )}
        </div>

        <div className="DeviceTimeWizard__footer border-top pt-4 mt-4">
          <Button
            size="small"
            color="secondary"
            title="Cancel"
            disabled={formik.isSubmitting}
            onClick={onClose}
          />

          <div className="DeviceTimeWizard__navigation">
            <Button
              size="small"
              color="outline-secondary"
              title="Back"
              className={
                activeStep === StepsEnum.SELECT_DEVICES ? "invisible" : ""
              }
              disabled={formik.isSubmitting}
              onClick={() => setActiveStep(StepsEnum.SELECT_DEVICES)}
            />
            {activeStep === StepsEnum.SELECT_DEVICES ? (
              <Button
                size="small"
                color="primary"
                title="Next"
                disabled={!selectedDevices.length}
                onClick={() => setActiveStep(StepsEnum.CONFIGURE)}
              />
            ) : (
              <Button
                size="small"
                color="primary"
                title="Save"
                loadTitle="Saving"
                disabled={formik.isSubmitting}
                loadSpinner={formik.isSubmitting}
                onClick={formik.submitForm}
              />
            )}
          </div>
        </div>

        {isPickerOpen ? (
          <>
            <DeviceTimePicker
              devices={devices}
              selectedDevices={selectedDevices}
              onChange={setSelectedDevices}
              onClose={closePicker}
            />
            <div onClick={closePicker} className="background__shadow" />
          </>
        ) : null}
      </div>
    </Modal>
  );
};
